import { User, Users, Building, Zap, Globe } from 'lucide-react';
import { cn } from '@/lib/utils';

export type TodoScope = 
  | 'personal_crm' 
  | 'personal_charging' 
  | 'team_crm' 
  | 'team_charging' 
  | 'team_all'
  | 'location'
  | 'project'
  | 'opportunity';

interface ScopeOption {
  value: TodoScope;
  label: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

interface TodoScopeSelectorProps {
  value: TodoScope;
  onChange: (scope: TodoScope) => void;
  isChargingUser: boolean;
  hasLocation?: boolean;
  hasProject?: boolean;
  hasOpportunity?: boolean;
  disabled?: boolean;
}

export function TodoScopeSelector({ 
  value, 
  onChange, 
  isChargingUser,
  hasLocation,
  hasProject,
  hasOpportunity,
  disabled 
}: TodoScopeSelectorProps) {
  // If context is set, show that scope and don't allow changes
  if (hasLocation) {
    return (
      <div className="p-3 rounded-lg border bg-muted/50">
        <div className="flex items-center gap-2 text-sm">
          <Zap className="h-4 w-4 text-primary" />
          <span className="font-medium">Locatie-taak</span>
          <span className="text-muted-foreground">– Zichtbaar voor iedereen met toegang tot deze locatie</span>
        </div>
      </div>
    );
  }

  if (hasProject) {
    return (
      <div className="p-3 rounded-lg border bg-muted/50">
        <div className="flex items-center gap-2 text-sm">
          <Building className="h-4 w-4 text-primary" />
          <span className="font-medium">Project-taak</span>
          <span className="text-muted-foreground">– Zichtbaar voor CRM gebruikers</span>
        </div>
      </div>
    );
  }

  if (hasOpportunity) {
    return (
      <div className="p-3 rounded-lg border bg-muted/50">
        <div className="flex items-center gap-2 text-sm">
          <Building className="h-4 w-4 text-primary" />
          <span className="font-medium">Opportunity-taak</span>
          <span className="text-muted-foreground">– Zichtbaar voor Charging gebruikers</span>
        </div>
      </div>
    );
  }

  // Build scope options based on user role
  const scopeOptions: ScopeOption[] = isChargingUser ? [
    {
      value: 'personal_charging',
      label: 'Persoonlijk',
      description: 'Alleen voor jou zichtbaar',
      icon: <User className="h-4 w-4" />,
      color: 'border-blue-500 bg-blue-500/10',
    },
    {
      value: 'team_charging',
      label: 'Charging Team',
      description: 'Zichtbaar voor alle Charging gebruikers',
      icon: <Zap className="h-4 w-4" />,
      color: 'border-yellow-500 bg-yellow-500/10',
    },
    {
      value: 'team_all',
      label: 'Iedereen',
      description: 'Zichtbaar voor CRM + Charging',
      icon: <Globe className="h-4 w-4" />,
      color: 'border-green-500 bg-green-500/10',
    },
  ] : [
    {
      value: 'personal_crm',
      label: 'Persoonlijk',
      description: 'Alleen voor jou zichtbaar',
      icon: <User className="h-4 w-4" />,
      color: 'border-blue-500 bg-blue-500/10',
    },
    {
      value: 'team_crm',
      label: 'CRM Team',
      description: 'Zichtbaar voor alle CRM gebruikers',
      icon: <Users className="h-4 w-4" />,
      color: 'border-purple-500 bg-purple-500/10',
    },
    {
      value: 'team_all',
      label: 'Iedereen',
      description: 'Zichtbaar voor CRM + Charging',
      icon: <Globe className="h-4 w-4" />,
      color: 'border-green-500 bg-green-500/10',
    },
  ];

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">Zichtbaarheid</label>
      <div className="grid grid-cols-3 gap-2">
        {scopeOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            disabled={disabled}
            onClick={() => onChange(option.value)}
            className={cn(
              'flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-all',
              'hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/20',
              value === option.value ? option.color + ' border-2' : 'border-border bg-background',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            <div className={cn(
              'flex items-center justify-center w-8 h-8 rounded-full',
              value === option.value ? 'text-primary' : 'text-muted-foreground'
            )}>
              {option.icon}
            </div>
            <span className="text-sm font-medium">{option.label}</span>
            <span className="text-xs text-muted-foreground text-center leading-tight">
              {option.description}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
